// Global
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Local
import styles from './Header.module.scss';
import { PureHeaderPrimaryLinkBar, PureHeaderSectionLink } from './types';
import { HeaderMegaMenuContainer } from './HeaderMegaMenuContainer';
import { HeaderSubMenu } from './HeaderMobile/HeaderSubMenu';
import useClickOutside from 'lib/hooks/useClickOutside';
import { useIsTablet } from 'lib/hooks/useIsTablet';
import useDictionary from 'lib/hooks/useDictionary';
import { generateSubmenuId } from './Header.utils';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';
import { LinkContext } from 'components/helpers/Atoms/Cta/CtaLink';
interface HeaderPrimaryLinkBarProps {
  primaryLinkBar: PureHeaderPrimaryLinkBar[];
  mobileNavOpen: boolean;
  setMobileNavOpen: (mobileNavOpen: boolean) => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  linkContext: LinkContext;
}

export const HeaderPrimaryLinkBar = (props: HeaderPrimaryLinkBarProps) => {
  const navRef = useRef<HTMLElement>(null);
  const firstInteractiveElementRef = useRef<HTMLButtonElement>(null);
  const { getDictionaryValue } = useDictionary();

  // Setup state for accordion behavior - track which accordion is open
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(null);

  // Setup state for submenu
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [selectedSectionLink, setSelectedSectionLink] = useState<PureHeaderSectionLink | null>(
    null
  );
  const isTablet = useIsTablet();

  const navElement = navRef.current as HTMLElement | null;
  const buttonElement = props.buttonRef.current as HTMLElement | null;

  const setOpenSubMenuEvent = (sectionLink?: PureHeaderSectionLink) => {
    if (sectionLink) {
      setSelectedSectionLink(sectionLink);
      setOpenSubMenu(true);
    } else {
      setOpenSubMenu(false);
      setSelectedSectionLink(null);
    }
  };

  // Keep focus on the toggle button when mobile nav opens so its expanded
  // state is announced; Tab naturally moves focus into the revealed nav.
  useLayoutEffect(() => {
    if (!props.mobileNavOpen) {
      setOpenAccordionIndex(null);
      setOpenSubMenu(false);
      setSelectedSectionLink(null);
    }
  }, [props.mobileNavOpen]);

  // Watch for outside focus and outside clicks
  useEffect(() => {
    if (props.mobileNavOpen) {
      document.addEventListener('focusin', handleFocusChange);
      document.addEventListener('keydown', handleEscapeKeyPress);
    }

    return () => {
      // Cleanup Function
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('keydown', handleEscapeKeyPress);
    };
  });

  // Use click outside hook for nav only
  useClickOutside(navRef, props.mobileNavOpen, () => props.setMobileNavOpen(false));

  // Additional click handler to prevent closing when clicking the button
  useEffect(() => {
    const handleButtonClick = (event: MouseEvent) => {
      const buttonElement = props.buttonRef.current as HTMLElement | null;
      if (buttonElement && buttonElement.contains(event.target as Node)) {
        // Prevent the useClickOutside from closing the menu
        event.stopPropagation();
      }
    };

    if (props.mobileNavOpen) {
      document.addEventListener('mousedown', handleButtonClick, true); // Use capture phase
    }

    return () => {
      document.removeEventListener('mousedown', handleButtonClick, true);
    };
  }, [props.mobileNavOpen, props.buttonRef]);

  // If focus isn't in the nav anymore, close mobile nav
  function handleFocusChange(e: FocusEvent) {
    // Return early if focus has left the document (aka dev tools, etc)
    if (!document.hasFocus()) {
      return;
    }

    // Check if focus is still within the nav or has moved to the hamburger button
    const isFocusInNav = navElement && navElement.contains(e.target as Node);
    const isFocusOnButton = buttonElement && buttonElement.contains(e.target as Node);

    if (!isFocusInNav && !isFocusOnButton) {
      props.setMobileNavOpen(false);
    }
  }

  function handleEscapeKeyPress(e: KeyboardEvent) {
    // Early return if not escape key
    if (e.key !== 'Escape') {
      return;
    }

    // If focus is inside the nav, focus button before closing
    const navElement = navRef.current as HTMLElement | null;
    if (navElement && navElement.contains(e.target as Node)) {
      const buttonElement = props.buttonRef.current as HTMLElement | null;
      if (buttonElement) {
        buttonElement.setAttribute('aria-expanded', 'false');
        buttonElement.focus();
      }
    }

    props.setMobileNavOpen(false);
  }

  // Handle accordion toggle - close others when one opens
  const handleAccordionToggle = (index: number) => {
    setOpenAccordionIndex(openAccordionIndex === index ? null : index);
  };

  const handleCloseMenu = () => {
    props.setMobileNavOpen(false);
    setOpenSubMenuEvent();
    setOpenAccordionIndex(null);
  };

  useOnRouteChange(handleCloseMenu);

  return (
    <nav
      id={'nav'}
      ref={navRef}
      className={props.mobileNavOpen ? styles.openDrawer : styles.closedDrawer}
      aria-label={getDictionaryValue('Navigation.Header.MainNavigation') || 'header'}
    >
      <div className={styles.megaNavDrawer}>
        {/* Hide primary links when submenu is open */}
        {(!openSubMenu || !isTablet) && (
          <ul className={styles.headerPrimaryLinkBarList} id="header-primary-link-bar-list">
            {props?.primaryLinkBar?.map((item, index) => (
              <HeaderMegaMenuContainer
                key={index}
                isMobileMegaNavOpen={props.mobileNavOpen}
                primaryLinkBar={item}
                isOpen={openAccordionIndex === index}
                onToggle={() => handleAccordionToggle(index)}
                openSubMenu={openSubMenu}
                setOpenSubMenu={setOpenSubMenuEvent}
                selectedSectionLink={selectedSectionLink}
                firstInteractiveElementRef={index === 0 ? firstInteractiveElementRef : undefined}
                primaryLinkBarIndex={index}
                linkContext={props.linkContext}
              />
            ))}
          </ul>
        )}

        {/* Submenu rendered parallel to primaryLinkBarList (For mobile) */}
        {openSubMenu &&
          props.mobileNavOpen &&
          selectedSectionLink &&
          isTablet &&
          (() => {
            // Find the index of the primary link bar that contains the selected section link
            const primaryLinkBarIndex = props.primaryLinkBar.findIndex((primaryLinkBar) =>
              primaryLinkBar.sectionLinks?.some(
                (sectionLink) =>
                  sectionLink.sectionLinkLabel === selectedSectionLink.sectionLinkLabel
              )
            );
            // Find the index of the section link within the primary link bar
            const sectionLinkIndex =
              props.primaryLinkBar[primaryLinkBarIndex]?.sectionLinks?.findIndex(
                (sectionLink) =>
                  sectionLink.sectionLinkLabel === selectedSectionLink.sectionLinkLabel
              ) || 0;
            return (
              <HeaderSubMenu
                sectionLink={selectedSectionLink}
                onBack={() => {
                  setOpenSubMenuEvent();
                }}
                firstInteractiveElementRef={firstInteractiveElementRef}
                submenuId={generateSubmenuId(
                  selectedSectionLink,
                  primaryLinkBarIndex,
                  sectionLinkIndex
                )}
                linkContext={props.linkContext}
              />
            );
          })()}
      </div>
    </nav>
  );
};
